import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminAudit() {
  const { isAdmin } = useApp();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [signAttempts, setSignAttempts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('stats');
  const [filter, setFilter] = useState({ action: '', days: 7 });

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, filter.days]);

  async function loadData() {
    setLoading(true);
    try {
      const [statsRes, logsRes, signsRes] = await Promise.all([
        api.get(`/admin/audit/stats?days=${filter.days}`),
        api.get(`/admin/audit/logs?days=${filter.days}&limit=50`),
        api.get(`/admin/audit/sign-attempts?days=${filter.days}`),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs);
      setSignAttempts(signsRes.data);
    } catch (e) {
      console.error('Audit load error:', e);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  }

  const StatCard = ({ icon, label, value, sub }) => (
    <div className="stat-card" style={{
      background: 'rgba(108,92,231,0.15)',
      borderRadius: 12,
      padding: 16,
      textAlign: 'center',
      flex: 1,
      minWidth: 80,
    }}>
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, opacity: 0.5 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="page"><BackButton />
      <h1>📋 Audit Logs</h1>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[7, 14, 30].map(d => (
          <button
            key={d}
            className={`btn btn-small ${filter.days === d ? 'active' : ''}`}
            onClick={() => setFilter({ ...filter, days: d })}
            style={{
              background: filter.days === d ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 20,
            }}
          >
            {d} дней
          </button>
        ))}
        <button className="btn btn-small" onClick={loadData}>🔄</button>
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : (
        <>
          {/* Stats cards */}
          {stats && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <StatCard icon="📊" label="Всего" value={stats.total_actions} />
              <StatCard icon="✅" label="Успех" value={stats.success_count} sub={`${stats.success_rate}%`} />
              <StatCard icon="❌" label="Ошибки" value={stats.error_count} />
              <StatCard icon="⚡" label="Latency" value={stats.avg_latency_ms || '—'} sub="ms" />
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { id: 'stats', label: '📈 Статистика' },
              { id: 'signs', label: '✍️ Подписи' },
              { id: 'logs', label: '📜 Логи' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`btn ${tab === opt.id ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTab(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === 'stats' && stats && (
            <div className="audit-stats">
              <h3>По типу действия:</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(stats.by_action || {}).map(([action, count]) => (
                  <div key={action} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: 8,
                  }}>
                    <span>{action}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'signs' && signAttempts && (
            <div className="sign-attempts">
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <StatCard icon="✍️" label="Попыток" value={signAttempts.total_attempts} />
                <StatCard icon="✅" label="Успешных" value={signAttempts.successful} />
                <StatCard icon="❌" label="Неудачных" value={signAttempts.failed} />
              </div>

              {Object.keys(signAttempts.error_reasons || {}).length > 0 && (
                <>
                  <h3>Причины ошибок:</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {Object.entries(signAttempts.error_reasons).map(([reason, count]) => (
                      <div key={reason} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: 'rgba(255,107,107,0.15)',
                        borderRadius: 8,
                      }}>
                        <span style={{ fontSize: 13 }}>{reason}</span>
                        <span style={{ fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {signAttempts.recent_failures?.length > 0 && (
                <>
                  <h3>Последние ошибки:</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {signAttempts.recent_failures.map((f, i) => (
                      <div key={i} style={{
                        padding: 12,
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}>
                        <div>👤 User: {f.user_id} | TG: {f.telegram_id}</div>
                        <div style={{ color: '#ff6b6b' }}>❌ {f.reason}</div>
                        <div style={{ fontSize: 11, opacity: 0.6 }}>
                          {f.request_id} • {new Date(f.created_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'logs' && (
            <div className="audit-logs">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {logs.map(log => (
                  <div key={log.id} style={{
                    padding: 12,
                    background: log.success ? 'rgba(0,206,201,0.1)' : 'rgba(255,107,107,0.1)',
                    borderRadius: 8,
                    borderLeft: `3px solid ${log.success ? '#00cec9' : '#ff6b6b'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>{log.action}</span>
                      <span style={{ fontSize: 11, opacity: 0.6 }}>
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      User: {log.user_id} | {log.endpoint} | {log.latency_ms}ms
                    </div>
                    {log.details && (
                      <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>
                        {log.details}
                      </div>
                    )}
                    <div style={{ fontSize: 11, marginTop: 4, opacity: 0.5 }}>
                      ID: {log.request_id} | IP: {log.ip_address}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
